import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Heart } from "lucide-react";
import { api, track } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LessonCard } from "./learn/LessonCard";
import { CourseGrid } from "./learn/CourseGrid";
import { CollectionTabs } from "./learn/CollectionTabs";
import { CollectionLanding } from "./learn/CollectionLanding";
import { CollectionCard } from "./learn/CollectionCard";
import { CategoryHero } from "./learn/CategoryHero";
import { FiltersToolbar } from "./learn/FiltersToolbar";
import { LearnStats } from "./learn/LearnStats";
import { LearningPath } from "./learn/LearningPath";
import { CourseManager } from "./learn/CourseManager";

export default function Learn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [favCourses, setFavCourses] = useState(new Set());
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [lengthF, setLengthF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [collView, setCollView] = useState(null);
  const [manageCourses, setManageCourses] = useState(false);
  const isOwner = user?.role === "owner";

  const load = () => api.get("/lessons").then(({ data }) => setLessons(data)).catch(() => {});
  const loadCats = () => api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  const loadFavs = () => api.get("/courses/favorites").then(({ data }) => setFavCourses(new Set(data))).catch(() => {});
  useEffect(() => {
    track("learn_viewed");
    loadCats();
    load();
    loadFavs();
  }, []);

  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const otherCats = useMemo(() => cats.filter((c) => !c.collection), [cats]);
  const collections = useMemo(() => {
    const map = {};
    cats.forEach((c) => {
      if (c.collection) {
        map[c.collection] = map[c.collection] || { id: c.collection, title: c.collection_title, cats: [] };
        map[c.collection].cats.push(c);
      }
    });
    const arr = Object.values(map);
    const pref = user?.preferred_collection;
    if (pref && pref !== "all") arr.sort((a, b) => (a.id === pref ? -1 : b.id === pref ? 1 : 0));
    return arr;
  }, [cats, user]);
  const countFor = (cid) => lessons.filter((l) => l.category_id === cid).length;
  const lockedCats = useMemo(() => new Set(lessons.filter((l) => l.locked).map((l) => l.category_id)), [lessons]);
  const lockedLessonIds = useMemo(() => new Set(lessons.filter((l) => l.locked).map((l) => l.id)), [lessons]);

  const promptSubscribe = () => {
    toast("Unlock all of Sisterhood University", { description: "This course is part of full membership. Subscribe to open every course." });
    navigate("/paywall");
  };

  const toggleFav = async (id) => {
    const { data } = await api.post(`/lessons/${id}/bookmark`);
    setLessons((ls) => ls.map((l) => (l.id === id ? { ...l, bookmarked: data.bookmarked } : l)));
  };
  const openLesson = (id) => { if (lockedLessonIds.has(id)) return promptSubscribe(); navigate(`/lesson/${id}`); };
  const openCourse = (id) => { if (lockedCats.has(id)) return promptSubscribe(); setActive(id); };

  const toggleCourseFav = async (id) => {
    const { data } = await api.post(`/courses/${id}/favorite`);
    setFavCourses((prev) => {
      const next = new Set(prev);
      if (data.favorited) next.add(id); else next.delete(id);
      return next;
    });
  };
  const savedCats = useMemo(() => cats.filter((c) => favCourses.has(c.id)), [cats, favCourses]);
  const savedLessons = useMemo(() => lessons.filter((l) => l.bookmarked), [lessons]);

  const filtered = useMemo(() => {
    let list = [...lessons];
    if (active !== "all") list = list.filter((l) => l.category_id === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((l) => l.title.toLowerCase().includes(q) || l.summary.toLowerCase().includes(q));
    }
    if (lengthF !== "all") list = list.filter((l) => (lengthF === "short" ? l.minutes <= 5 : lengthF === "medium" ? l.minutes > 5 && l.minutes <= 8 : l.minutes > 8));
    if (statusF === "completed") list = list.filter((l) => l.completed);
    if (statusF === "saved") list = list.filter((l) => l.bookmarked);
    if (statusF === "not_started") list = list.filter((l) => !l.completed);
    return list;
  }, [lessons, active, query, lengthF, statusF]);

  const stats = useMemo(() => {
    const completed = lessons.filter((l) => l.completed).length;
    const saved = lessons.filter((l) => l.bookmarked).length;
    return { total: lessons.length, completed, saved, pct: lessons.length ? Math.round((completed / lessons.length) * 100) : 0 };
  }, [lessons]);

  const activeCat = catById[active];
  const activeCollection = collView ? collections.find((c) => c.id === collView) : null;

  return (
    <div className="space-y-8" data-testid="learn-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Sisterhood University</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1">Learn at your own pace</h1>
        <p className="text-[#291B2D]/60 mt-2 max-w-2xl">Short, substantive lessons for real life — reflect, practice, and carry them into your day.</p>
      </div>

      <LearnStats stats={stats} areaCount={cats.length} onOpenSaved={() => setStatusF("saved")} />

      <div data-testid="university-disclaimer" className="rounded-2xl bg-[#F7F2EA] border border-[#E5DED4] p-5 lg:p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold flex items-center gap-2">
          <Heart className="h-4 w-4" /> A gentle note
        </p>
        <p className="text-sm text-[#59535B] mt-2 leading-relaxed max-w-3xl">
          Every woman's story is her own. These journeys are written with deep care and research — but no single
          lesson can hold the full richness or nuance of your life, culture, or community, and we won't get every
          detail perfect. Think of them as a starting place, not the final word. Take what resonates, gently leave
          what doesn't, and trust your own truth above all. You are the expert on you.
        </p>
      </div>

      <FiltersToolbar
        query={query} setQuery={setQuery}
        showFilters={showFilters} setShowFilters={setShowFilters}
        lengthF={lengthF} setLengthF={setLengthF}
        statusF={statusF} setStatusF={setStatusF}
      />

      {activeCat && <CategoryHero activeCat={activeCat} active={active} onBack={() => setActive("all")} />}

      {(active !== "all" || query.trim()) && (
        <p className="text-sm text-[#291B2D]/50">Showing {filtered.length} of {lessons.length} lessons</p>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 card-soft text-center">
          <p className="font-heading text-xl">No lessons match your filters</p>
          <p className="text-sm text-[#291B2D]/60 mt-2">Try clearing a filter or the search.</p>
        </div>
      ) : active === "all" ? (
        query.trim() ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="lessons-grid">
            {filtered.map((l) => {
              const cat = catById[l.category_id];
              const Icon = Icons[cat?.icon] || Icons.Sparkles;
              return <LessonCard key={l.id} l={l} catName={cat?.title || ""} Icon={Icon} onFav={toggleFav} onOpen={openLesson} />;
            })}
          </div>
        ) : (
          <div className="space-y-10">
            <CollectionTabs collections={collections} collView={collView} setCollView={setCollView} savedCount={savedCats.length + savedLessons.length} />

            {collView === "saved" ? (
              <div data-testid="saved-courses-view" className="space-y-10">
                {savedCats.length === 0 && savedLessons.length === 0 ? (
                  <div className="bg-white rounded-3xl p-12 card-soft text-center" data-testid="saved-empty">
                    <p className="font-heading text-xl">Nothing saved yet</p>
                    <p className="text-sm text-[#291B2D]/60 mt-2">Tap the heart on a course or the star on a lesson to save it here for later.</p>
                  </div>
                ) : (
                  <>
                    {savedCats.length > 0 && (
                      <div>
                        <h2 className="font-heading text-2xl mb-4">Saved courses</h2>
                        <CourseGrid cats={savedCats} countFor={countFor} onOpen={openCourse} testId="saved-courses"
                          favIds={favCourses} onFav={toggleCourseFav} lockedIds={lockedCats} />
                      </div>
                    )}
                    {savedLessons.length > 0 && (
                      <div>
                        <h2 className="font-heading text-2xl mb-4">Saved lessons</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="saved-lessons">
                          {savedLessons.map((l) => {
                            const cat = catById[l.category_id];
                            const Icon = Icons[cat?.icon] || Icons.Sparkles;
                            return <LessonCard key={l.id} l={l} catName={cat?.title || ""} Icon={Icon} onFav={toggleFav} onOpen={openLesson} />;
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : collView === "featured" ? (
              <div data-testid="featured-only">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-2xl">Explore what connects us</h2>
                  {isOwner && (
                    <button onClick={() => setManageCourses(true)} data-testid="manage-courses-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#291B2D] text-white px-4 py-2 text-sm font-medium hover:brightness-110 transition-smooth">
                      Manage courses
                    </button>
                  )}
                </div>
                <CourseGrid cats={otherCats} countFor={countFor} onOpen={openCourse} testId="featured-courses"
                  favIds={favCourses} onFav={toggleCourseFav} lockedIds={lockedCats} />
              </div>
            ) : activeCollection ? (
              <CollectionLanding coll={activeCollection} lessons={lessons} countFor={countFor} onStartLesson={openLesson} onOpenCourse={openCourse} />
            ) : (
              <>
                <div>
                  <h2 className="font-heading text-2xl">Explore what connects us</h2>
                  <p className="text-sm text-[#291B2D]/55 mt-1 mb-4">Growth for the experiences women share.</p>
                  <CourseGrid cats={otherCats} countFor={countFor} onOpen={openCourse} testId="featured-courses"
                    favIds={favCourses} onFav={toggleCourseFav} lockedIds={lockedCats} />
                </div>

                {collections.length > 0 && (
                  <div>
                    <h2 className="font-heading text-2xl">Explore what shapes us</h2>
                    <p className="text-sm text-[#291B2D]/55 mt-1">Culturally responsive collections for particular communities and lived experiences — open to all, with respect for the stories being centered.</p>
                  </div>
                )}
                {collections.map((coll) => (
                  <CollectionCard key={coll.id} coll={coll} user={user} countFor={countFor} onExplore={setCollView} onOpenCourse={openCourse} />
                ))}
              </>
            )}
          </div>
        )
      ) : (
        <LearningPath filtered={filtered} catById={catById} onFav={toggleFav} onOpen={openLesson} />
      )}

      {isOwner && (
        <CourseManager
          open={manageCourses}
          onClose={() => setManageCourses(false)}
          onSaved={() => { loadCats(); load(); }}
        />
      )}
    </div>
  );
}
